import React, { useState, useEffect } from "react";
import instance from "../apiInstance";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHeart, faStar, faTags, faUsers, faFilter } from "@fortawesome/free-solid-svg-icons";
import { Modal } from "react-bootstrap";
import toast from "react-hot-toast";
import "./FavoritesManagement.css";

const CATEGORY_LABEL = {
  bolsas: "Bolsas", malas: "Malas de Viagem", relogios: "Relógios",
  acessorios: "Acessórios", joias: "Joias", roupas: "Roupas",
  calcados: "Calçados", outros: "Outros",
};

const fmtPrice = (v) => v ? `R$ ${Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 0 })}` : "—";
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("pt-BR") : "—";

const FavoritesManagement = ({ openUserModal }) => {
  const [favorites, setFavorites] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState("");
  const [activeView, setActiveView] = useState("list"); // "list" | "summary"
  const [selectedImage, setSelectedImage] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCategory) params.category = filterCategory;

      const [favRes, sumRes] = await Promise.all([
        instance.get("/admin/favorites/", { params }),
        instance.get("/admin/favorites/summary"),
      ]);
      setFavorites(favRes.data);
      setSummary(sumRes.data);
    } catch {
      toast.error("Erro ao carregar favoritos");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, [filterCategory]);

  return (
    <div className="fav-root">
      <div className="fav-header">
        <div>
          <h2><FontAwesomeIcon icon={faHeart} style={{ color: "#e74c3c", marginRight: 8 }} />Favoritos das Clientes</h2>
          <p>Produtos que as clientes salvaram nas promos — use para criar ofertas personalizadas.</p>
        </div>
        <div className="fav-header-actions">
          <button className={`fav-view-btn ${activeView === "list" ? "active" : ""}`} onClick={() => setActiveView("list")}>
            <FontAwesomeIcon icon={faUsers} /> Lista
          </button>
          <button className={`fav-view-btn ${activeView === "summary" ? "active" : ""}`} onClick={() => setActiveView("summary")}>
            <FontAwesomeIcon icon={faStar} /> Resumo
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="fav-filters">
        <FontAwesomeIcon icon={faFilter} style={{ color: "#888" }} />
        <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} className="fav-select">
          <option value="">Todas as categorias</option>
          {Object.entries(CATEGORY_LABEL).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        {summary && (
          <span className="fav-total">{summary.total} favoritos no total</span>
        )}
      </div>

      {loading ? (
        <div className="fav-loading">Carregando...</div>
      ) : activeView === "summary" ? (
        <div className="fav-summary-grid">
          {/* Top Categorias */}
          <div className="fav-summary-card">
            <h4><FontAwesomeIcon icon={faTags} /> Top Categorias</h4>
            {summary?.top_categories?.length === 0 && <p className="fav-empty-text">Sem dados ainda</p>}
            {summary?.top_categories?.map((c, i) => (
              <div key={i} className="fav-rank-item">
                <div className="fav-rank-bar-wrap">
                  <span className="fav-rank-label">{CATEGORY_LABEL[c.name] || c.name}</span>
                  <div className="fav-rank-bar" style={{ width: `${(c.count / (summary.top_categories[0]?.count || 1)) * 100}%` }} />
                </div>
                <span className="fav-rank-count">{c.count}</span>
              </div>
            ))}
          </div>

          {/* Top Marcas */}
          <div className="fav-summary-card">
            <h4><FontAwesomeIcon icon={faStar} /> Top Marcas</h4>
            {summary?.top_brands?.length === 0 && <p className="fav-empty-text">Sem dados ainda</p>}
            {summary?.top_brands?.map((b, i) => (
              <div key={i} className="fav-rank-item">
                <div className="fav-rank-bar-wrap">
                  <span className="fav-rank-label">{b.name}</span>
                  <div className="fav-rank-bar" style={{ width: `${(b.count / (summary.top_brands[0]?.count || 1)) * 100}%` }} />
                </div>
                <span className="fav-rank-count">{b.count}</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="fav-table-wrap">
          {favorites.length === 0 ? (
            <div className="fav-empty">
              <FontAwesomeIcon icon={faHeart} style={{ fontSize: 32, opacity: 0.2 }} />
              <span>Nenhum favorito ainda.</span>
            </div>
          ) : (
            <table className="fav-table">
              <thead>
                <tr>
                  <th>Produto</th>
                  <th>Marca</th>
                  <th>Categoria</th>
                  <th>Promo</th>
                  <th>Preço orig.</th>
                  <th>Cliente</th>
                  <th>Data</th>
                </tr>
              </thead>
              <tbody>
                {favorites.map(fav => (
                  <tr key={fav.id}>
                    <td>
                      <div className="fav-product-cell">
                        {fav.snapshot_image_url && (
                          <img 
                            src={fav.snapshot_image_url} 
                            alt="" 
                            className="fav-thumb" 
                            style={{ cursor: "pointer" }}
                            onClick={() => setSelectedImage(fav.snapshot_image_url)}
                            onError={e => e.target.style.display = "none"} 
                          />
                        )}
                        <span>{fav.snapshot_title || "—"}</span>
                      </div>
                    </td>
                    <td>{fav.snapshot_brand || "—"}</td>
                    <td>
                      <span className="fav-badge-category">
                        {CATEGORY_LABEL[fav.snapshot_category] || fav.snapshot_category || "—"}
                      </span>
                    </td>
                    <td>{fav.snapshot_promo_title || "—"}</td>
                    <td>{fmtPrice(fav.snapshot_original_price)}</td>
                    <td>
                      <div 
                        className="fav-user-cell" 
                        style={{ cursor: openUserModal ? "pointer" : "default" }}
                        onClick={() => openUserModal && openUserModal({
                          id: fav.user_id,
                          name: fav.users?.name,
                          email: fav.users?.email,
                          phone: fav.users?.phone,
                          created_at: fav.users?.created_at,
                          instagram_handle: fav.users?.instagram_handle,
                          gender: fav.users?.gender,
                          points: fav.users?.points,
                          birth_date: fav.users?.birth_date,
                          preferences: fav.users?.preferences,
                        })}
                      >
                        <span style={{ color: openUserModal ? "#B8962E" : "inherit", fontWeight: openUserModal ? "600" : "normal" }}>
                          {fav.users?.name || "—"}
                        </span>
                        {fav.users?.email && <small style={{ color: "#888" }}>{fav.users.email}</small>}
                        {fav.users?.phone && <small style={{ color: "#888" }}>{fav.users.phone}</small>}
                      </div>
                    </td>
                    <td>{fmtDate(fav.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <Modal show={!!selectedImage} onHide={() => setSelectedImage(null)} centered size="lg">
        <Modal.Header closeButton style={{ borderBottom: 'none', paddingBottom: 0 }}>
        </Modal.Header>
        <Modal.Body style={{ padding: '0 20px 20px 20px', textAlign: 'center' }}>
          {selectedImage && (
            <img 
              src={selectedImage} 
              alt="Favorito ampliado" 
              style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain', borderRadius: '8px' }} 
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default FavoritesManagement;
